import AttorneyHubAPIService from "_shared/_api/attorney-hub-api-service";
import PaginatedResponse from "_shared/_api/pagination/responses/paginated-response";
import axios, { AxiosHeaders } from "axios";
import PermissionTemplateCommand from "permissions/_api/request-contracts/permission-template-command";
import PermissionTemplateQuery from "permissions/_api/request-contracts/permission-template-query";
import PermissionTemplateSchema, {
  PermissionTemplateJSON,
} from "permissions/templates/_schemas/permission-template-schema";
import PermissionTemplate from "permissions/templates/permission-template";
import Session from "users/__session/session";

export default class PermissionTemplatesAPIService {
  public constructor(session: Readonly<Session>) {
    if (!session.authToken) {
      throw new Error("Cannot create PermissionsTemplateAPIService without session.");
    }
    this.headers.set("Authorization", `Bearer ${session.authToken.value}`);
  }

  public async addPermissionTemplate(
    template: PermissionTemplate,
    abortController?: AbortController,
  ): Promise<PermissionTemplateJSON> {
    const response = await axios.post(
      `${AttorneyHubAPIService.apiBaseUrl}/permissions/templates`,
      new PermissionTemplateCommand(template),
      {
        headers: this.headersWithJson(),
        signal: abortController?.signal,
      },
    );

    return PermissionTemplateSchema.validateJSON(response.data);
  }

  public async getPermissionTemplates(
    query: PermissionTemplateQuery,
    abortController?: AbortController,
  ): Promise<PaginatedResponse<PermissionTemplateJSON>> {
    const response = await axios.get(
      `${AttorneyHubAPIService.apiBaseUrl}/permissions/templates?${query.searchParams.toString()}`,
      {
        headers: this.headers,
        signal: abortController?.signal,
      },
    );

    const responseData = response.data.map((p: unknown) => PermissionTemplateSchema.validateJSON(p));

    return PaginatedResponse.from<PermissionTemplateJSON>(responseData, response.headers["x-pagination"]);
  }

  public async getPermissionTemplateById(
    id: string,
    abortController?: AbortController,
  ): Promise<PermissionTemplateJSON> {
    const response = await axios.get(`${AttorneyHubAPIService.apiBaseUrl}/permissions/templates/${id}`, {
      headers: this.headers,
      signal: abortController?.signal,
    });

    return PermissionTemplateSchema.validateJSON(response.data);
  }

  public async updatePermissionTemplate(
    template: PermissionTemplate,
    abortController?: AbortController,
  ): Promise<PermissionTemplateJSON> {
    const response = await axios.put(
      `${AttorneyHubAPIService.apiBaseUrl}/permissions/templates/${template.id}`,
      new PermissionTemplateCommand(template),
      {
        headers: this.headersWithJson(),
        signal: abortController?.signal,
      },
    );

    return PermissionTemplateSchema.validateJSON(response.data);
  }

  public async deletePermissionTemplateById(id: string, abortController?: AbortController): Promise<void> {
    await axios.delete(`${AttorneyHubAPIService.apiBaseUrl}/permissions/templates/${id}`, {
      headers: this.headers,
      signal: abortController?.signal,
    });
  }

  private readonly headers: AxiosHeaders = new AxiosHeaders();

  private headersWithJson(): AxiosHeaders {
    return this.headers.concat({ "Content-Type": "application/json-patch+json" });
  }
}

export class PermissionsTemplateAPIServiceError extends Error {
  public methodName: string;
  public error: unknown;

  constructor(methodName: string, error: unknown) {
    super(`Error in PermissionsTemplateAPIService.${methodName}: ${error}`);
    this.methodName = methodName;
    this.error = error;
  }
}
